import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { useEffect, useState } from 'react';
import type { OrganizationResponse } from '@bm/contracts';
import { api } from '../lib/api';
import { useAuth } from '../lib/auth';
import { Alert, Button, Card, Field, Input } from '../components/ui';
import { OrgTabs } from '../components/OrgTabs';

const FIELDS: Array<[keyof OrganizationResponse, string]> = [
  ['name', 'Vereinsname'],
  ['shortName', 'Kurzname'],
  ['street', 'Straße'],
  ['houseNumber', 'Hausnummer'],
  ['zip', 'PLZ'],
  ['city', 'Ort'],
  ['country', 'Land (2-stellig)'],
  ['phone', 'Telefon'],
  ['email', 'E-Mail'],
  ['website', 'Website'],
  ['contactPerson', 'Ansprechpartner'],
  ['clubNumber', 'Vereinsnummer'],
  ['federation', 'Verband'],
];

export function OrgSettingsPage() {
  const qc = useQueryClient();
  const { can } = useAuth();
  const { data } = useQuery({
    queryKey: ['org'],
    queryFn: () => api<OrganizationResponse>('/org', { orgScoped: true }),
  });
  const [form, setForm] = useState<Record<string, string>>({});
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    if (data) {
      const next: Record<string, string> = {};
      for (const [key] of FIELDS) next[key] = (data[key] as string | null) ?? '';
      setForm(next);
    }
  }, [data]);

  const mutation = useMutation({
    mutationFn: (body: Record<string, string>) =>
      api<OrganizationResponse>('/org', { method: 'PATCH', body, orgScoped: true }),
    onSuccess: () => {
      setSaved(true);
      void qc.invalidateQueries({ queryKey: ['org'] });
      setTimeout(() => setSaved(false), 2500);
    },
  });

  const readOnly = !can('organization.edit');

  return (
    <div className="space-y-4">
      <h1 className="hidden text-xl font-semibold md:block">Verein</h1>
      <OrgTabs />
      <Card title="Vereinsdaten">
        {saved && <Alert kind="success">Gespeichert.</Alert>}
        <form
          className="grid grid-cols-1 gap-4 sm:grid-cols-2"
          onSubmit={(e) => {
            e.preventDefault();
            const body = Object.fromEntries(Object.entries(form).filter(([, v]) => v !== ''));
            mutation.mutate(body);
          }}
        >
          {FIELDS.map(([key, label]) => (
            <Field key={key} label={label}>
              <Input
                value={form[key] ?? ''}
                disabled={readOnly}
                onChange={(e) => setForm((f) => ({ ...f, [key]: e.target.value }))}
              />
            </Field>
          ))}
          {!readOnly && (
            <div className="sm:col-span-2">
              <Button type="submit" disabled={mutation.isPending}>
                Speichern
              </Button>
            </div>
          )}
        </form>
      </Card>
    </div>
  );
}
